"use client";

import React from "react";
import clsx from "clsx";
import {MdDone} from "react-icons/md";
import {TbPinnedFilled} from "react-icons/tb";
import {
    NotificationCategoryBadge,
} from "@/components/notifications/NotificationCategoryBadge";
import {NotificationDescription} from "@/components/notifications/NotificationDescription";
import type {Locale} from "@/i18n-config";
import {InlineText} from "@/components/content/common/InlineText";

export function NotificationItem({
    id,
    locale,
    publishedAt,
    readAt,
    icon,
    categoryKey,
    label,
    title,
    description,
    metadata,
    action,
    sticky = false,
    stickyLabel,
    markReadLabel,
    showMoreLabel,
    showLessLabel,
    onMarkRead,
}: {
    id: string;
    locale: Locale;
    publishedAt: string;
    readAt: string | null;
    icon: React.ReactNode;
    categoryKey?: string;
    label?: React.ReactNode;
    title: React.ReactNode;
    description?: string;
    metadata?: React.ReactNode;
    action?: React.ReactNode;
    sticky?: boolean;
    stickyLabel: string;
    markReadLabel: string;
    showMoreLabel: string;
    showLessLabel: string;
    onMarkRead: (notificationId: string) => void | Promise<void>;
}) {
    const unread = !readAt;

    return (
        <article
            className={clsx(
                "relative flex gap-3 border-b border-zinc-200 px-4 py-4 last:border-b-0",
                unread ? "bg-grass-500/[0.07]" : "bg-white",
            )}
        >
            <div className="mt-0.5 flex shrink-0 flex-col gap-2">
                <div
                    className={clsx(
                        "flex size-10 items-center justify-center rounded-full",
                        categoryKey === "warning"
                            ? "bg-rose-700/10 text-rose-700"
                            : unread
                                ? "bg-grass-500 text-white"
                                : "bg-zinc-100 text-zinc-600",
                    )}
                    aria-hidden="true"
                >
                    {icon}
                </div>
                {sticky && (
                    <div className="flex size-10 items-center justify-center rounded-full bg-zinc-100 text-zinc-600">
                        <TbPinnedFilled
                            aria-label={stickyLabel}
                            className="size-5"
                        />
                    </div>
                )}
            </div>

            <div className="min-w-0 flex-1 pr-6">
                {label && (
                    <NotificationCategoryBadge
                        categoryKey={categoryKey}
                        className="mb-2 text-xs uppercase tracking-wide"
                    >
                        {label}
                    </NotificationCategoryBadge>
                )}
                <h3 className="text-base font-semibold leading-tight text-zinc-900">
                    {typeof title === "string" ? <InlineText value={title} /> : title}
                </h3>
                {description && (
                    <NotificationDescription
                        description={description}
                        showMoreLabel={showMoreLabel}
                        showLessLabel={showLessLabel}
                    />
                )}
                {metadata && (
                    <div className="mt-2 text-xs text-zinc-500">
                        {metadata}
                    </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                    <time
                        dateTime={publishedAt}
                        suppressHydrationWarning
                        className="text-xs text-zinc-400"
                    >
                        {new Intl.DateTimeFormat(locale, {
                            dateStyle: "medium",
                            timeStyle: "short",
                        }).format(new Date(publishedAt))}
                    </time>
                    {action}
                </div>
            </div>

            {unread && (
                <button
                    type="button"
                    aria-label={markReadLabel}
                    title={markReadLabel}
                    onClick={() => onMarkRead(id)}
                    className="group absolute right-3 top-3 flex size-7 items-center justify-center rounded-full text-grass-700 transition-colors hover:bg-grass-500/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grass-500"
                >
                    <span className="size-2 rounded-full bg-grass-500 group-hover:opacity-0 group-focus-visible:opacity-0" />
                    <MdDone className="absolute size-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                </button>
            )}
        </article>
    );
}
